import {
  Button, Form, Input, Spin, Modal, Tag, Divider, Tooltip, Row, Col, message, Select,
} from "antd";
import {
  QrcodeOutlined, ScanOutlined, DownloadOutlined, CheckCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import Webcam from "react-webcam";
import dayjs from "dayjs";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { fetchBindingByUuid, fetchBindingReport } from "../service/checklistBinding";

// eslint-disable-next-line
let jsQR = null;
// eslint-disable-next-line
try { jsQR = require("jsqr"); } catch (e) { /* QR scanning disabled if jsqr not installed */ }

const { Option } = Select;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const decodeMeta   = (s)   => { try { return JSON.parse(s); } catch { return null; } };
const toRawBase64  = (src) => { if (!src) return null; const i = src.indexOf(","); return i >= 0 ? src.slice(i + 1) : src; };
const detectExt    = (src) => { if (!src) return "jpeg"; if (src.startsWith("data:image/png")) return "png"; return "jpeg"; };

const INPUT_TYPE_LABEL = {
  STRING: "Text", DOUBLE: "Number", DATE: "Date",
  OK_NOT_OK: "OK / Not OK", OPTIONS: "Options", LIVE_IMAGE: "Live Image",
};

// ─── QR Scanner helpers ───────────────────────────────────────────────────────

// CHANGE 1: preprocessCanvas() — boost contrast before passing pixels to jsQR.
// Why: jsQR decodes raw RGBA pixels. A 5mm QR code at arm's length occupies
// very few pixels and auto-exposure / white-balance makes the modules blurry
// and low-contrast. Boosting contrast before decoding dramatically improves
// hit-rate — this is what native scanner apps do internally.
// We compute min/max luminance across the frame (BT.601) then stretch to
// [0, 255] and apply a mild S-curve to sharpen module edges.
const preprocessCanvas = (ctx, w, h) => {
  const imageData = ctx.getImageData(0, 0, w, h);
  const data = imageData.data;
  let minL = 255, maxL = 0;
  for (let i = 0; i < data.length; i += 4) {
    const l = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    if (l < minL) minL = l;
    if (l > maxL) maxL = l;
  }
  const range = maxL - minL || 1;
  for (let i = 0; i < data.length; i += 4) {
    const l = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    let v = ((l - minL) / range) * 255;
    // S-curve: push values toward extremes (sharpen module edges)
    v = v < 128
      ? (v / 128) * (v / 128) * 128
      : 255 - ((255 - v) / 128) * ((255 - v) / 128) * 128;
    v = Math.max(0, Math.min(255, Math.round(v)));
    data[i] = v; data[i + 1] = v; data[i + 2] = v;
  }
  ctx.putImageData(imageData, 0, 0);
};

// CHANGE 2: tryDecodeMultiScale() — attempt jsQR at three zoom levels.
// Why: A 5mm QR code occupies a tiny region of the full camera frame. jsQR
// searches the entire image which means the QR modules are just a few pixels
// each and the finder-pattern detection fails. By also trying 2× and 4×
// centre crops we give jsQR a frame where the QR fills more of the image —
// exactly what native scanner apps do with their "zoom into centre" heuristic.
const tryDecodeMultiScale = (ctx, fullW, fullH) => {
  if (!jsQR) return null;

  const crops = [
    { x: 0,           y: 0,           w: fullW,       h: fullH       }, // 1× full frame
    { x: fullW * 0.25, y: fullH * 0.25, w: fullW * 0.5, h: fullH * 0.5 }, // 2× centre crop
    { x: fullW * 0.375, y: fullH * 0.375, w: fullW * 0.25, h: fullH * 0.25 }, // 4× centre crop
  ];

  for (const crop of crops) {
    const cw = Math.round(crop.w);
    const ch = Math.round(crop.h);
    if (cw < 50 || ch < 50) continue;

    const offscreen = document.createElement("canvas");
    offscreen.width = cw;
    offscreen.height = ch;
    const offCtx = offscreen.getContext("2d", { willReadFrequently: true });
    offCtx.drawImage(
      ctx.canvas,
      Math.round(crop.x), Math.round(crop.y), cw, ch,
      0, 0, cw, ch,
    );

    // Apply contrast preprocessing to each crop independently
    preprocessCanvas(offCtx, cw, ch);

    const imageData = offCtx.getImageData(0, 0, cw, ch);
    const code = jsQR(imageData.data, imageData.width, imageData.height, {
      inversionAttempts: "attemptBoth",
    });
    if (code?.data) return code;
  }
  return null;
};

// Reconstruct a display grid from template records + observations
const buildDisplayGrid = (records, observations) => {
  if (!records?.length) return { grid: [], colCount: 1 };

  const hasMeta  = records.some((r) => r.gridMeta);
  const obsMap   = new Map((observations || []).map((o) => [o.recordId, o]));

  if (!hasMeta) {
    return {
      colCount: 1,
      grid: records.map((r) => ({
        rowId: `r${r.id}`,
        cells: [{ ...r, colSpan: 1, rowSpan: 1, hidden: false,
          obs: obsMap.get(r.id) || null,
          sampleImages: (r.sampleImages || []).map((img) => ({
            url: typeof img === "string" ? `data:image/png;base64,${img}` : img,
          })),
        }],
      })),
    };
  }

  const maxRi    = Math.max(...records.map((r) => decodeMeta(r.gridMeta)?.ri ?? 0)) + 1;
  const colCount = Math.max(...records.map((r) => {
    const m = decodeMeta(r.gridMeta); return m ? m.ci + (m.colSpan || 1) : 1;
  }));

  const makeEmpty = (ri, ci) => ({
    id: `e_${ri}_${ci}`, ri, ci, colSpan: 1, rowSpan: 1, hidden: false,
    cellType: "DESCRIPTION", description: "", inputType: "STRING",
    obs: null, sampleImages: [], liveImages: [],
  });

  const grid = Array.from({ length: maxRi }, (_, ri) => ({
    rowId: `row_${ri}`,
    cells: Array.from({ length: colCount }, (_, ci) => makeEmpty(ri, ci)),
  }));

  records.forEach((r) => {
    const meta = decodeMeta(r.gridMeta);
    if (!meta) return;
    const cell = grid[meta.ri]?.cells[meta.ci];
    if (!cell) return;
    const cellType = meta.cellType || (meta.isHeader ? "HEADER" : "DESCRIPTION");
    Object.assign(cell, {
      ...r,
      ri: meta.ri, ci: meta.ci,
      colSpan:  meta.colSpan || 1,
      rowSpan:  meta.rowSpan || 1,
      cellType,
      label:    meta.label || "",
      isMandatory: r.isMandatory || meta.isMandatory || false,
      obs: obsMap.get(r.id) || null,
      sampleImages: (r.sampleImages || []).map((img) => ({
        url: typeof img === "string" ? `data:image/png;base64,${img}` : img,
      })),
      liveImages: obsMap.get(r.id)?.liveImages || [],
    });
    for (let dr = 0; dr < (meta.rowSpan || 1); dr++)
      for (let dc = 0; dc < (meta.colSpan || 1); dc++) {
        if (dr === 0 && dc === 0) continue;
        const c = grid[meta.ri + dr]?.cells[meta.ci + dc];
        if (c) c.hidden = true;
      }
  });

  return { grid, colCount };
};

// ─── Main component ───────────────────────────────────────────────────────────
const BindingReport = () => {
  const { uuid }   = useParams();
  const navigate   = useNavigate();
  const [form]     = Form.useForm();

  const [binding,       setBinding]      = useState(null);
  const [report,        setReport]       = useState(null);
  const [loading,       setLoading]      = useState(false);
  const [qrOpen,        setQrOpen]       = useState(false);
  const [qrError,       setQrError]      = useState("");
  const [previewSrc,    setPreviewSrc]   = useState(null);
  const [messageApi,    contextHolder]   = message.useMessage();

  // ── Camera selection state ─────────────────────────────────────────────
  const [qrUseExact,   setQrUseExact]   = useState(true);
  const [qrDeviceId,   setQrDeviceId]   = useState(null);
  const [qrDeviceList, setQrDeviceList] = useState([]);
  const [qrDeviceIdx,  setQrDeviceIdx]  = useState(0);

  // CHANGE 3: Added qrDebugInfo state for live scanner feedback.
  // Why: Without feedback the user has no idea if the scanner is working or
  // if they need to hold the phone differently. "Scanning… (frame N)"
  // reassures them the scanner is active.
  const [qrDebugInfo,  setQrDebugInfo]  = useState("");
  // FIX: On desktop/laptop there is typically only one camera (the front
  // webcam). The "last device = rear" heuristic that works on phones silently
  // picks the front camera on desktops. We expose a manual camera-picker
  // dropdown so the user can always choose the right one.
  const [showCameraPicker, setShowCameraPicker] = useState(false);

  const qrWebcamRef  = useRef(null);
  // CHANGE 4: Replaced setInterval with requestAnimationFrame refs.
  // Why: setInterval fires independently of the browser's render cycle,
  // causing frame-tearing and wasted CPU on frames the GPU hasn't finished
  // compositing yet. RAF is GPU-synced, smoother, and automatically pauses
  // when the tab is hidden (saving battery on mobile).
  const qrRafRef     = useRef(null);
  const qrCanvasRef  = useRef(null);
  // CHANGE 5: Frame throttle counter — only decode every 3rd RAF tick.
  // Why: Running jsQR + multi-scale crops at 60fps causes thermal throttling
  // on phones, which makes the camera drop to a lower quality mode mid-scan.
  // ~20fps decode rate prevents this while still feeling real-time.
  const qrFrameCount = useRef(0);

  // Load binding metadata on mount
  useEffect(() => {
    fetchBindingByUuid(uuid)
      .then((r) => setBinding(r.data))
      .catch(() => message.error("Failed to load binding details"));
  }, [uuid]);

  // ── Camera enumeration ──────────────────────────────────────────────────
  // FIX: Previous version silently fell back to "last device = rear" which
  // is correct on phones but wrong on desktops (only one front webcam).
  // New strategy: only suppress the picker when we find a camera explicitly
  // labelled back/rear/environment; otherwise always show the picker.
  const detectCameras = useCallback(async () => {
    try {
      const tmpStream = await navigator.mediaDevices.getUserMedia({ video: true });
      tmpStream.getTracks().forEach((t) => t.stop());

      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter((d) => d.kind === "videoinput");
      setQrDeviceList(videoInputs);

      if (videoInputs.length === 0) { setQrDeviceId(null); return; }

      const back = videoInputs.find((d) => /back|rear|environment/i.test(d.label || ""));

      if (back) {
        // Confident label match — no picker needed.
        const idx = videoInputs.findIndex((d) => d.deviceId === back.deviceId);
        setQrDeviceId(back.deviceId);
        setQrDeviceIdx(idx >= 0 ? idx : 0);
        setShowCameraPicker(false);
      } else {
        // Cannot confidently determine rear camera — default to last device
        // and show the picker so the user can correct it.
        const chosen = videoInputs[videoInputs.length - 1];
        setQrDeviceId(chosen.deviceId);
        setQrDeviceIdx(videoInputs.length - 1);
        setShowCameraPicker(true);
      }
    } catch (e) {
      setQrDeviceId(null);
      setQrDeviceList([]);
      setShowCameraPicker(false);
    }
  }, []);

  const switchCamera = useCallback(() => {
    if (qrDeviceList.length < 2) return;
    stopQr();
    const nextIdx = (qrDeviceIdx + 1) % qrDeviceList.length;
    setQrDeviceIdx(nextIdx);
    setQrDeviceId(qrDeviceList[nextIdx].deviceId);
  }, [qrDeviceList, qrDeviceIdx]);

  // CHANGE 6: enableTorch() — attempt to switch on the phone's flashlight
  // after the stream starts.
  // Why: Consistent close-range illumination is the single largest factor for
  // reading small QR codes. Auto-exposure without torch often causes the
  // camera to over- or under-expose the tiny code area. We silently ignore
  // errors because torch is not available on all devices/browsers.
  const enableTorch = useCallback(async () => {
    try {
      const video = qrWebcamRef.current?.video;
      if (!video) return;
      const stream = video.srcObject;
      if (!stream) return;
      const track = stream.getVideoTracks()[0];
      if (!track) return;
      const capabilities = track.getCapabilities?.();
      if (capabilities?.torch) {
        await track.applyConstraints({ advanced: [{ torch: true }] });
      }
    } catch { /* torch not supported — ignore */ }
  }, []);

  // ── QR scanner ─────────────────────────────────────────────────────────────
  //
  // CHANGE 7: Complete rewrite of startQr() — four key improvements over the
  // original setInterval + getScreenshot approach:
  //
  //   a) Uses requestAnimationFrame instead of setInterval(300ms).
  //      RAF is GPU-synced and fires at the display refresh rate; setInterval
  //      at 300ms means we miss ~17 frames between each decode attempt. RAF
  //      with a frame-count throttle gives us ~20 attempts/sec while staying
  //      in sync with the video compositor.
  //
  //   b) Reads directly from the <video> element via ctx.drawImage(video).
  //      The original code called getScreenshot() which re-encodes the frame
  //      to JPEG (lossy) then decodes it back to an Image then draws it to a
  //      canvas. Each JPEG round-trip blurs the image — fatal for tiny QRs.
  //      Direct video→canvas preserves full sensor sharpness.
  //
  //   c) MAX_DIM raised to 2000 (was effectively capped by screenshot size).
  //      Why: A 5mm QR at 15cm is ~30px in a 720p frame. At 2000px it's
  //      ~60px, and the 4× centre crop in tryDecodeMultiScale() brings it
  //      to ~240px — well within jsQR's reliable range.
  //
  //   d) Calls tryDecodeMultiScale() with preprocessing instead of a single
  //      raw-pixel jsQR call (see CHANGE 1 and CHANGE 2 above).
  //
  const startQr = useCallback(() => {
    setQrError("");
    setQrDebugInfo("Starting camera…");
    qrFrameCount.current = 0;

    if (!qrCanvasRef.current) {
      qrCanvasRef.current = document.createElement("canvas");
    }
    const canvas = qrCanvasRef.current;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    const tick = () => {
      const video = qrWebcamRef.current?.video;
      if (!video || video.readyState !== video.HAVE_ENOUGH_DATA) {
        setQrDebugInfo("Waiting for camera…");
        qrRafRef.current = requestAnimationFrame(tick);
        return;
      }

      // CHANGE 7c: MAX_DIM 2000 — preserve more sensor detail for small codes.
      const MAX_DIM = 2000;
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      if (!vw || !vh) { qrRafRef.current = requestAnimationFrame(tick); return; }

      const scale = Math.min(1, MAX_DIM / Math.max(vw, vh));
      const w = Math.round(vw * scale);
      const h = Math.round(vh * scale);

      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }

      // CHANGE 7b: draw video frame directly — no JPEG round-trip.
      ctx.drawImage(video, 0, 0, w, h);

      // CHANGE 5: throttle decode to every 3rd frame (~20fps).
      qrFrameCount.current += 1;
      if (qrFrameCount.current % 3 !== 0) {
        qrRafRef.current = requestAnimationFrame(tick);
        return;
      }

      setQrDebugInfo(`Scanning… (frame ${qrFrameCount.current})`);

      // CHANGE 7d: multi-scale decode with contrast preprocessing.
      const code = tryDecodeMultiScale(ctx, w, h);
      if (code?.data) {
        stopQr();
        fillQr(code.data);
        return;
      }

      qrRafRef.current = requestAnimationFrame(tick);
    };

    qrRafRef.current = requestAnimationFrame(tick);
  }, []);

  const stopQr = useCallback(() => {
    if (qrRafRef.current) {
      cancelAnimationFrame(qrRafRef.current);
      qrRafRef.current = null;
    }
    setQrDebugInfo("");
  }, []);

  const closeQr = () => { stopQr(); setQrOpen(false); };

  const openQr = () => {
    setQrError("");
    setQrDebugInfo("");
    setQrUseExact(true);
    setQrOpen(true);
    detectCameras();
  };

  const fillQr = (raw) => {
    const parts = raw.split("!");
    if (parts.length !== 3) {
      setQrError(`Invalid QR format — expected SERIAL!PO!MODEL. Got: "${raw}"`);
      startQr();
      return;
    }
    const [serial, po, model] = parts.map((p) => p.trim());
    form.setFieldsValue({ serialNumber: serial, poNumber: po, modelNumber: model });
    setQrOpen(false);
    messageApi.success(`QR scanned — Serial: ${serial} | PO: ${po} | Model: ${model}`);
  };

  // ── Fetch report ──────────────────────────────────────────────────────────
  const loadReport = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setLoading(true);
    try {
      const res = await fetchBindingReport(uuid, values);
      setReport(res.data);
    } catch (e) {
      messageApi.error(e?.response?.data?.message || "Failed to load report");
    } finally { setLoading(false); }
  };

  // ── Excel export ──────────────────────────────────────────────────────────
  const downloadExcel = async () => {
    if (!report) return;
    setLoading(true);
    try {
      const wb  = new ExcelJS.Workbook();
      const ws  = wb.addWorksheet("Binding Report");

      const getImageDimensions = (src) => new Promise((res) => {
        const img = new Image();
        img.onload  = () => res({ w: img.naturalWidth, h: img.naturalHeight });
        img.onerror = () => res({ w: 200, h: 150 });
        img.src = src;
      });

      const maxCols = Math.max(
        ...report.templates.map((tmpl) => {
          const { colCount } = buildDisplayGrid(tmpl.template?.records || [], []);
          return colCount;
        }),
        1
      );
      const COL_W = 28;
      ws.columns = Array.from({ length: maxCols }, () => ({ width: COL_W }));

      const thin   = { style: "thin",   color: { argb: "FFAAAAAA" } };
      const medium = { style: "medium",  color: { argb: "FF333333" } };
      const border = (t = "thin") => { const b = t === "medium" ? medium : thin; return { top: b, bottom: b, left: b, right: b }; };

      ws.mergeCells(1, 1, 1, maxCols);
      const titleCell = ws.getCell(1, 1);
      titleCell.value     = `Binding Report — ${report.bindingName || ""}`;
      titleCell.font      = { bold: true, size: 13, color: { argb: "FF1F3864" } };
      titleCell.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD6E4F0" } };
      titleCell.alignment = { vertical: "middle", horizontal: "center" };
      titleCell.border    = border("medium");
      ws.getRow(1).height = 26;

      const globalMeta = [
        ["Serial Number", report.serialNumber || ""],
        ["PO Number",     report.poNumber     || ""],
        ["Model Number",  report.modelNumber  || ""],
        ["Product",       report.product?.productName || ""],
        [`${report.templates?.filter(t => t.status === "COMPLETED").length} / ${report.templates?.length} completed`, ""],
      ];
      globalMeta.forEach(([label, val], i) => {
        const r  = 2 + i;
        const lc = ws.getCell(r, 1);
        const vc = ws.getCell(r, 2);
        lc.value = label; lc.font = { bold: true, size: 11 };
        vc.value = val;   vc.font = { size: 11 };
        [lc, vc].forEach((c) => { c.border = border(); c.alignment = { vertical: "middle" }; });
        ws.getRow(r).height = 18;
      });
      ws.getRow(7).height = 6;
      ws.getRow(8).height = 6;

      const allImageQueue = [];
      let cursor = 9;

      for (const tmpl of report.templates) {
        const records = tmpl.template?.records || [];
        const obsArr  = tmpl.execution?.observations || [];
        const { grid, colCount } = buildDisplayGrid(records, obsArr);

        ws.mergeCells(cursor, 1, cursor, maxCols);
        const divCell = ws.getCell(cursor, 1);
        divCell.value = `${tmpl.sequenceNo}. ${tmpl.checklistName}  ${tmpl.status === "COMPLETED" ? "✓ Completed" : "⏳ Pending"}${tmpl.execution?.createdDate ? "  |  " + dayjs(tmpl.execution.createdDate).format("DD/MM/YYYY HH:mm") : ""}`;
        divCell.font  = { bold: true, size: 12, color: { argb: tmpl.status === "COMPLETED" ? "FF237804" : "FFAD6800" } };
        divCell.fill  = { type: "pattern", pattern: "solid", fgColor: { argb: tmpl.status === "COMPLETED" ? "FFF6FFED" : "FFFFFBE6" } };
        divCell.alignment = { vertical: "middle", horizontal: "left", indent: 1 };
        divCell.border = border("medium");
        ws.getRow(cursor).height = 22;
        cursor++;

        if (!grid.length) {
          ws.mergeCells(cursor, 1, cursor, maxCols);
          const noteCell = ws.getCell(cursor, 1);
          noteCell.value = "Not yet executed for this unit.";
          noteCell.font  = { italic: true, color: { argb: "FFAD6800" }, size: 11 };
          noteCell.fill  = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFBE6" } };
          noteCell.alignment = { vertical: "middle", indent: 2 };
          ws.getRow(cursor).height = 18;
          cursor++;
        } else {
          for (const row of grid) {
            // ── FIX ──────────────────────────────────────────────────────
            // Column position must come from the cell's own index (ci),
            // NOT from a running counter that increments only for
            // *visible* cells. `row.cells` is always fully populated
            // (one entry per column, 0..colCount-1) by buildDisplayGrid,
            // so `ci` is always the correct sheet column.
            //
            // The old code used `let col = 1; ... col += cs;` and simply
            // `continue`d over hidden cells without advancing `col`. That
            // is fine when a cell is hidden by a *horizontal* colSpan
            // (the spanning cell earlier in the same row already
            // accounted for the width) but breaks when a cell is hidden
            // by a *vertical* rowSpan from a previous row: nothing in the
            // current row advances `col` for that gap, so every real cell
            // after it gets written one (or more) columns too far left.
            // That shifted cell then collides with the still-active
            // vertical merge, and ExcelJS throws
            // "Cannot merge already merged cells" — which aborts the
            // whole export (surfaced only as a generic
            // "Failed to generate Excel" toast).
            row.cells.forEach((cell, ci) => {
              if (cell.hidden) return;

              const col = ci + 1; // 1-based column, always in sync with the grid
              const cs  = cell.colSpan || 1;
              const rs  = cell.rowSpan || 1;

              try {
                if (cs > 1 || rs > 1)
                  ws.mergeCells(cursor, col, cursor + rs - 1, col + cs - 1);

                const wc = ws.getCell(cursor, col);
                wc.alignment = { vertical: "middle", wrapText: true };
                wc.border    = border();

                if (cell.cellType === "HEADER") {
                  wc.value = cell.label || cell.description || "";
                  wc.font  = { bold: true, size: 11, color: { argb: "FF1677FF" } };
                  wc.fill  = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE8F0FE" } };
                  wc.alignment = { vertical: "middle", horizontal: "center" };
                } else if (cell.cellType === "DESCRIPTION") {
                  wc.value = cell.description || "";
                  wc.fill  = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } };
                } else if (cell.cellType === "INPUT") {
                  wc.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFBE6" } };
                  const raw = cell.obs?.value;
                  let v;
                  if (raw === null || raw === undefined) {
                    v = null;
                  } else if (typeof raw === "string") {
                    try { v = JSON.parse(raw); } catch { v = raw; }
                  } else {
                    v = raw;
                  }
                  if (cell.inputType === "LIVE_IMAGE") {
                    wc.value = "";
                    (cell.liveImages || []).forEach((src) =>
                      allImageQueue.push({ sheetRow: cursor, col, cs, rs, src })
                    );
                  } else if (cell.inputType === "DATE") {
                    wc.value  = v ? String(v) : "";
                    wc.numFmt = "dd/mm/yyyy";
                  } else {
                    if (v === null || v === undefined) wc.value = "";
                    else if (typeof v === "boolean")   wc.value = v ? "OK" : "NOT OK";
                    else                               wc.value = String(v);
                  }
                } else if (cell.cellType === "IMAGE") {
                  wc.fill  = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF6FFED" } };
                  wc.value = "";
                  (cell.sampleImages || []).forEach((img) =>
                    allImageQueue.push({ sheetRow: cursor, col, cs, rs, src: img.url })
                  );
                }
              } catch (cellErr) {
                // Don't let one bad cell (e.g. unexpected overlapping
                // gridMeta from a data issue) kill the entire export —
                // log it and drop in a visible marker instead.
                console.error(
                  `Excel export: failed to write cell (template="${tmpl.checklistName}", row=${cursor}, col=${col}, recordId=${cell.id})`,
                  cellErr
                );
                const wc = ws.getCell(cursor, col);
                wc.value = wc.value || "⚠";
                wc.border = border();
              }
            });

            // Fill remaining columns up to maxCols with empty bordered cells
            for (let col = colCount + 1; col <= maxCols; col++) {
              ws.getCell(cursor, col).border = border();
            }
            ws.getRow(cursor).height = 20;
            cursor++;
          }
        }

        ws.getRow(cursor).height = 8;
        cursor++;
      }

      const cellMap = new Map();
      allImageQueue.forEach((item) => {
        const k = `${item.sheetRow}_${item.col}`;
        if (!cellMap.has(k)) cellMap.set(k, []);
        cellMap.get(k).push(item);
      });

      for (const [, items] of cellMap)
        await Promise.all(items.map(async (item) => { item.dims = await getImageDimensions(item.src); }));

      const rowMaxPx = new Map();
      for (const [, items] of cellMap)
        for (const { sheetRow, dims } of items) {
          const cur = rowMaxPx.get(sheetRow) || 0;
          if (dims.h > cur) rowMaxPx.set(sheetRow, dims.h);
        }

      for (const [sr, maxPx] of rowMaxPx)
        ws.getRow(sr).height = Math.min(Math.max(maxPx * 0.75, 60), 400);

      const COL_PX = COL_W * 7;
      for (const [, items] of cellMap) {
        const { sheetRow, col: sc, cs, rs } = items[0];
        const count   = items.length;
        const slotPx  = (COL_PX * cs) / count;
        const rowPx   = rowMaxPx.get(sheetRow) || 150;

        for (let idx = 0; idx < count; idx++) {
          const { src, dims } = items[idx];
          const raw = toRawBase64(src);
          const ext = detectExt(src);
          if (!raw) continue;
          const scaledH  = slotPx * (dims.h / dims.w);
          const rowFrac  = Math.min(scaledH / rowPx, 1) * rs;
          try {
            const imgId = wb.addImage({ base64: raw, extension: ext });
            ws.addImage(imgId, {
              tl: { col: sc - 1 + idx * (cs / count),       row: sheetRow - 1 },
              br: { col: sc - 1 + (idx + 1) * (cs / count), row: sheetRow - 1 + rowFrac },
              editAs: "oneCell",
            });
          } catch { ws.getCell(sheetRow, sc).value = `[${count} image(s)]`; }
        }
      }

      const buffer = await wb.xlsx.writeBuffer();
      const blob   = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const serial = report.serialNumber?.replace(/[/\\?*[\]]/g, "_") || "report";
      saveAs(blob, `binding_report_${serial}_${Date.now()}.xlsx`);
    } catch (e) {
      console.error(e);
      messageApi.error("Failed to generate Excel");
    } finally { setLoading(false); }
  };

  // ── Render helpers ────────────────────────────────────────────────────────
  const tdBase = { border: "1px solid #d9d9d9", verticalAlign: "top", padding: "6px 10px", boxSizing: "border-box" };

  const renderCell = (cell) => {
    const v = cell.obs?.value;
    switch (cell.cellType) {
      case "HEADER":
        return (
          <td key={cell.id} colSpan={cell.colSpan||1} rowSpan={cell.rowSpan||1}
            style={{ ...tdBase, background: "#e6f4ff", fontWeight: 700, color: "#1677ff", textAlign: "center", fontSize: 13 }}>
            {cell.label || cell.description}
          </td>
        );
      case "DESCRIPTION":
        return (
          <td key={cell.id} colSpan={cell.colSpan||1} rowSpan={cell.rowSpan||1}
            style={{ ...tdBase, background: "#fff", fontSize: 12 }}>
            {cell.description}
          </td>
        );
      case "INPUT": {
        const parseValue = (raw) => {
          if (raw === null || raw === undefined) return null;
          if (typeof raw === "string") {
            try { return JSON.parse(raw); } catch { return raw; }
          }
          return raw;
        };
        const displayVal = parseValue(v);

        let content;
        if (cell.inputType === "LIVE_IMAGE") {
          content = cell.liveImages?.length
            ? <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {cell.liveImages.map((src, i) => (
                  <img key={i} src={src} alt="captured"
                    style={{ width: 52, height: 52, objectFit: "cover", borderRadius: 3, cursor: "pointer", border: "1px solid #d9d9d9" }}
                    onClick={() => setPreviewSrc(src)} />
                ))}
              </div>
            : <span style={{ fontSize: 11, color: "#bbb" }}>No images captured</span>;
        } else if (cell.inputType === "OK_NOT_OK") {
          content = displayVal === null || displayVal === undefined
            ? <span style={{ color: "#bbb" }}>—</span>
            : <Tag color={displayVal === true || displayVal === "true" ? "green" : "red"}>
                {displayVal === true || displayVal === "true" ? "OK" : "NOT OK"}
              </Tag>;
        } else {
          content = displayVal !== null && displayVal !== undefined && displayVal !== ""
            ? <span style={{ fontSize: 13 }}>{String(displayVal)}</span>
            : <span style={{ color: "#bbb" }}>—</span>;
        }
        return (
          <td key={cell.id} colSpan={cell.colSpan||1} rowSpan={cell.rowSpan||1}
            style={{ ...tdBase, background: "#fffbe6" }}>
            {content}
          </td>
        );
      }
      case "IMAGE":
        return (
          <td key={cell.id} colSpan={cell.colSpan||1} rowSpan={cell.rowSpan||1}
            style={{ ...tdBase, background: "#f6ffed" }}>
            {cell.sampleImages?.length
              ? <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  <div style={{ fontSize: 10, color: "#888", width: "100%", marginBottom: 2 }}>Reference</div>
                  {cell.sampleImages.map((img, i) => (
                    <img key={i} src={img.url} alt="ref"
                      style={{ width: 52, height: 52, objectFit: "cover", borderRadius: 3, cursor: "pointer", border: "1px solid #b7eb8f" }}
                      onClick={() => setPreviewSrc(img.url)} />
                  ))}
                </div>
              : <span style={{ fontSize: 11, color: "#bbb" }}>No reference images</span>}
          </td>
        );
      default:
        return <td key={cell.id} colSpan={cell.colSpan||1} rowSpan={cell.rowSpan||1} style={{ ...tdBase }} />;
    }
  };

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <Spin spinning={loading}>
      {contextHolder}

      {/* Image preview */}
      <Modal open={!!previewSrc} footer={null} onCancel={() => setPreviewSrc(null)} title="Preview">
        <img src={previewSrc} alt="preview" style={{ width: "100%" }} />
      </Modal>

      {/* QR scanner */}
      <Modal
        open={qrOpen}
        title={<span><QrcodeOutlined /> Scan QR Code</span>}
        onCancel={closeQr}
        // CHANGE 8: afterOpenChange now also calls enableTorch() after the
        // webcam starts. We delay 1.2s so the stream is fully negotiated
        // before we try to apply advanced constraints.
        afterOpenChange={(o) => {
          if (o) {
            startQr();
            setTimeout(enableTorch, 1200);
          } else {
            stopQr();
          }
        }}
        footer={[
          ...(qrDeviceList.length > 1
            ? [<Button key="switch" onClick={switchCamera}>Switch Camera</Button>]
            : []),
          <Button key="c" onClick={closeQr}>Close</Button>,
        ]}
        width={460}
      >
        <div style={{ textAlign: "center" }}>

          {/* FIX: Camera picker — shown when auto-detection cannot confidently
              identify the rear camera (desktop with front webcam, or Android
              with generic "Camera 0/1" labels). */}
          {showCameraPicker && qrDeviceList.length > 0 && (
            <div style={{
              marginBottom: 10, padding: "8px 12px", borderRadius: 6,
              background: "#fffbe6", border: "1px solid #ffe58f",
              fontSize: 12, color: "#876800", textAlign: "left",
            }}>
              <div style={{ marginBottom: 6, fontWeight: 600 }}>
                📷 Select camera — auto-detection could not identify the rear camera:
              </div>
              <Select
                size="small"
                style={{ width: "100%" }}
                value={qrDeviceId}
                onChange={(deviceId) => {
                  stopQr();
                  const idx = qrDeviceList.findIndex((d) => d.deviceId === deviceId);
                  setQrDeviceId(deviceId);
                  setQrDeviceIdx(idx >= 0 ? idx : 0);
                }}
              >
                {qrDeviceList.map((d, i) => (
                  <Option key={d.deviceId} value={d.deviceId}>
                    {d.label || `Camera ${i + 1}`}
                  </Option>
                ))}
              </Select>
              <div style={{ marginTop: 4, fontSize: 11, color: "#ad6800" }}>
                On a phone: choose the <strong>back / rear</strong> camera. On a desktop: use a phone for best results.
              </div>
            </div>
          )}

          {/* FIX: Overlay wraps the Webcam so position:absolute sits on top of
              the video. Previously the overlay div was AFTER the Webcam div so
              it rendered below the video feed instead of over it. */}
          {qrOpen && (
            <div style={{ position: "relative", display: "inline-block", width: "100%", lineHeight: 0 }}>
              <Webcam
                key={qrDeviceId || (qrUseExact ? "qr-cam-exact" : "qr-cam-fallback")}
                ref={qrWebcamRef}
                screenshotFormat="image/jpeg"
                videoConstraints={
                  qrDeviceId
                    ? {
                        deviceId: { exact: qrDeviceId },
                        width:  { ideal: 4096 },
                        height: { ideal: 2160 },
                      }
                    : qrUseExact
                      ? {
                          facingMode: { exact: "environment" },
                          width:  { ideal: 4096 },
                          height: { ideal: 2160 },
                        }
                      : {
                          facingMode: "environment",
                          width:  { ideal: 4096 },
                          height: { ideal: 2160 },
                        }
                }
                onUserMediaError={() => {
                  if (qrDeviceId) { setQrDeviceId(null); setQrUseExact(true); return; }
                  if (qrUseExact) setQrUseExact(false);
                }}
                style={{ width: "100%", borderRadius: 6, display: "block" }}
              />
              {/* Aiming crosshair — position:absolute over the video */}
              <div style={{
                position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                pointerEvents: "none", borderRadius: 6,
              }}>
                <div style={{
                  position: "absolute", top: "50%", left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: 160, height: 160,
                }}>
                  {[
                    { top: 0,    left: 0,  borderTop: "3px solid #1677ff", borderLeft:  "3px solid #1677ff" },
                    { top: 0,    right: 0, borderTop: "3px solid #1677ff", borderRight: "3px solid #1677ff" },
                    { bottom: 0, left: 0,  borderBottom: "3px solid #1677ff", borderLeft:  "3px solid #1677ff" },
                    { bottom: 0, right: 0, borderBottom: "3px solid #1677ff", borderRight: "3px solid #1677ff" },
                  ].map((s, i) => (
                    <div key={i} style={{ position: "absolute", width: 28, height: 28, ...s }} />
                  ))}
                </div>
              </div>
            </div>
          )}

          <div style={{ marginTop: 6, fontSize: 12, color: "#888" }}>
            Centre the QR code within the brackets — hold steady, 10–15 cm away
          </div>

          {/* Live scan status */}
          {qrDebugInfo && (
            <div style={{ marginTop: 4, fontSize: 11, color: "#1677ff" }}>
              {qrDebugInfo}
            </div>
          )}

          <div style={{ marginTop: 6, fontSize: 11, color: "#888" }}>
            Format: <code>SERIAL!PO!MODEL</code>
          </div>
          {qrError && (
            <div style={{
              color: "#f5222d", marginTop: 6, fontSize: 12,
              background: "#fff2f0", borderRadius: 4,
              padding: "6px 10px", border: "1px solid #ffccc7",
            }}>
              {qrError}
            </div>
          )}
        </div>
      </Modal>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <div style={{display:"flex", alignItems:"center", gap:6}}>
          <div style={{ fontWeight: 700, fontSize: "1rem" }}>Binding Report : </div>
          {binding && <div style={{ fontWeight: 700, fontSize: "1rem" }}>{binding.bindingName} — {binding.product?.productName}</div>}
        </div>
      </div>

      {/* Entry form */}
      <div style={{ background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: 8, padding: "16px 20px", marginBottom: 20 }}>
        <Form form={form} layout="vertical">
          <Row gutter={12} align="bottom">
            <Col span={5}>
              <Form.Item name="serialNumber" label="Serial Number" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                <Input placeholder="BZCW220296" />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="poNumber" label="PO Number" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                <Input placeholder="815136556" />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="modelNumber" label="Model Number" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                <Input placeholder="X990790" />
              </Form.Item>
            </Col>
            <Col>
              <Tooltip title="Scan QR to auto-fill">
                <Button icon={<ScanOutlined />} style={{ marginRight: 8 }} onClick={openQr}>
                  Scan QR
                </Button>
              </Tooltip>
              <Button type="primary" onClick={loadReport}>Load Report</Button>
            </Col>
          </Row>
        </Form>
      </div>

      {/* Report content */}
      {report && (
        <>
          {/* Summary bar */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Tag color="default">Serial: {report.serialNumber}</Tag>
              <Tag color="default">PO: {report.poNumber}</Tag>
              <Tag color="default">Model: {report.modelNumber}</Tag>
              <Tag color="blue">{report.templates?.filter((t) => t.status === "COMPLETED").length} / {report.templates?.length} completed</Tag>
            </div>
            <Button type="primary" icon={<DownloadOutlined />} onClick={downloadExcel}>
              Download Excel
            </Button>
          </div>

          {/* Single continuous table — all templates in sequence */}
          {(() => {
            const maxCols = Math.max(
              ...(report.templates || []).map((tmpl) => {
                const { colCount } = buildDisplayGrid(tmpl.template?.records || [], []);
                return colCount;
              }),
              1
            );

            return (
              <div style={{ overflowX: "auto", border: "1px solid #d9d9d9", borderRadius: 8 }}>
                <table style={{ borderCollapse: "collapse", tableLayout: "fixed", width: "100%", minWidth: maxCols * 180 }}>
                  <thead>
                    <tr>
                      {Array.from({ length: maxCols }, (_, ci) => (
                        <th key={ci} style={{ background: "#f0f0f0", border: "1px solid #d9d9d9", padding: "4px 8px", fontSize: 11, fontWeight: 600, textAlign: "center", color: "#666" }}>
                          {String.fromCharCode(65 + ci)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(report.templates || []).map((tmpl) => {
                      const { grid, colCount } = buildDisplayGrid(
                        tmpl.template?.records || [],
                        tmpl.execution?.observations || []
                      );

                      return (
                        <React.Fragment key={tmpl.checklistId}>
                          {/* Template divider row */}
                          <tr>
                            <td colSpan={maxCols} style={{
                              padding: "8px 14px",
                              background: tmpl.status === "COMPLETED" ? "#f6ffed" : "#fffbe6",
                              borderTop: "2px solid #d9d9d9",
                              borderBottom: "1px solid #d9d9d9",
                            }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <Tag color="blue" style={{ fontWeight: 700, fontSize: 12 }}>#{tmpl.sequenceNo}</Tag>
                                <span style={{ fontWeight: 600, fontSize: 13 }}>{tmpl.checklistName}</span>
                                {tmpl.status === "COMPLETED"
                                  ? <Tag icon={<CheckCircleOutlined />} color="success">Completed</Tag>
                                  : <Tag icon={<ClockCircleOutlined />} color="warning">Pending</Tag>}
                                {tmpl.execution?.createdDate && (
                                  <span style={{ fontSize: 11, color: "#888", marginLeft: "auto" }}>
                                    Executed: {dayjs(tmpl.execution.createdDate).format("DD/MM/YYYY HH:mm")}
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>

                          {/* Pending note row */}
                          {tmpl.status === "PENDING" && (
                            <tr>
                              <td colSpan={maxCols} style={{ padding: "6px 16px", background: "#fffbe6", fontSize: 12, color: "#ad6800", borderBottom: "1px solid #ffe58f" }}>
                                This checklist has not been executed yet for this unit.
                              </td>
                            </tr>
                          )}

                          {/* Template grid rows */}
                          {grid.map((row) => (
                            <tr key={row.rowId}>
                              {row.cells.filter((c) => !c.hidden).map((cell) => renderCell(cell))}
                              {colCount < maxCols && (
                                <td colSpan={maxCols - colCount}
                                  style={{ border: "1px solid #f0f0f0", background: "#fafafa" }} />
                              )}
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </>
      )}
    </Spin>
  );
};

export default BindingReport;